import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { doEspaco } from '@/conversas/etiquetas';
import {
  atualizarRespostaRapida,
  criarRespostaRapida,
  ouvirRespostasRapidas,
  paraDocumentoResposta,
  removerRespostaRapida,
} from '@/services/conversasService';

/**
 * Respostas rápidas de UM setor — vive dentro de Preferências.
 *
 * O seletor `/` do Composer já existia e já consome esta coleção; **nada nele
 * foi tocado**. O que faltava era criar uma sem mexer no banco, e agora quem
 * cria é a própria equipe do setor.
 *
 * O `espaco` vem da ROTA, não de um seletor — mesmo motivo do SecaoEtiquetas:
 * oferecer o outro setor produziria um `permission-denied` que a tela não
 * deveria ter deixado acontecer.
 */

const DICA_VARIAVEIS = '{{primeiro_nome}} · {{nome}} · {{clinica}} · {{atendente}}';

function Formulario({ dados, aoMudar, aoSalvar, aoCancelar, salvando, rotuloSalvar, sufixo }) {
  // A prévia do atalho mostra o que o atendente vai digitar de fato: o service
  // sane a barra e os espaços, e ver "/meu-texto" evita a surpresa de descobrir
  // no Composer que o atalho ficou diferente do que se escreveu.
  const previa = paraDocumentoResposta(dados).atalho;

  return (
    <div className="resposta-form">
      <div className="linha-campos">
        <div className="campo">
          <label htmlFor={`rr-atalho-${sufixo}`}>Atalho</label>
          <input
            id={`rr-atalho-${sufixo}`}
            type="text"
            value={dados.atalho}
            onChange={(e) => aoMudar({ ...dados, atalho: e.target.value })}
            placeholder="endereco"
            disabled={salvando}
          />
          <p className="campo-ajuda">
            {previa ? (
              <>
                No inbox: <code>{previa}</code>
              </>
            ) : (
              'Uma palavra, sem espaço.'
            )}
          </p>
        </div>

        <div className="campo cresce">
          <label htmlFor={`rr-titulo-${sufixo}`}>Título</label>
          <input
            id={`rr-titulo-${sufixo}`}
            type="text"
            value={dados.titulo}
            onChange={(e) => aoMudar({ ...dados, titulo: e.target.value })}
            placeholder="Endereço da clínica"
            disabled={salvando}
          />
          <p className="campo-ajuda">Aparece ao lado do atalho na lista do Composer.</p>
        </div>

      </div>

      <div className="campo">
        <label htmlFor={`rr-texto-${sufixo}`}>Texto</label>
        <textarea
          id={`rr-texto-${sufixo}`}
          rows={3}
          value={dados.texto}
          onChange={(e) => aoMudar({ ...dados, texto: e.target.value })}
          placeholder="Estamos na Av. Exemplo, 1000. Referência: em frente ao parque."
          disabled={salvando}
        />
      </div>

      <div className="item-acoes">
        <button
          type="button"
          className="btn-primario compacto"
          onClick={aoSalvar}
          disabled={salvando || !dados.atalho.trim() || !dados.texto.trim()}
        >
          {salvando ? 'Salvando…' : rotuloSalvar}
        </button>
        {aoCancelar && (
          <button type="button" className="btn-texto" onClick={aoCancelar} disabled={salvando}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}

export default function SecaoRespostasRapidas({ espaco }) {
  const { clinicaId } = useAuth();
  const VAZIO = { atalho: '', titulo: '', texto: '', espaco };

  const [respostas, setRespostas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const [nova, setNova] = useState(VAZIO);
  const [editando, setEditando] = useState(null);
  // Confirmação INLINE, nunca `window.confirm`: o alerta nativo trava a janela.
  const [confirmando, setConfirmando] = useState(null);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirRespostasRapidas(
      clinicaId,
      (lista) => {
        setRespostas(
          doEspaco(lista, espaco).sort((a, b) => (a.atalho ?? '').localeCompare(b.atalho ?? '')),
        );
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ler respostas rápidas:', e);
        setErro('Não foi possível carregar as respostas rápidas.');
        setCarregando(false);
      },
    );
  }, [clinicaId, espaco]);

  async function salvar(dados, idAtual) {
    const limpo = paraDocumentoResposta(dados);

    // Atalho repetido não quebra nada, mas o seletor mostraria duas linhas
    // idênticas e a escolha viraria loteria.
    const repetido = respostas.some((r) => r.id !== idAtual && r.atalho === limpo.atalho);
    if (repetido) {
      setErro(`Já existe uma resposta com o atalho ${limpo.atalho}.`);
      return;
    }

    setErro(null);
    setSalvando(true);
    try {
      if (idAtual) await atualizarRespostaRapida(clinicaId, idAtual, dados);
      else await criarRespostaRapida(clinicaId, dados);
      setEditando(null);
      if (!idAtual) setNova(VAZIO);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar resposta rápida:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Seu papel não pode alterar as respostas deste setor.'
          : (e?.message ?? 'Não foi possível salvar a resposta.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  async function remover(resposta) {
    setSalvando(true);
    setErro(null);
    try {
      await removerRespostaRapida(clinicaId, resposta.id);
      setConfirmando(null);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao remover resposta rápida:', e);
      setErro('Não foi possível remover a resposta.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Respostas rápidas</h2>
          <p className="secao-sub">
            O atendente digita <code>/</code> no inbox deste setor e escolhe uma destas.
          </p>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      <div className="cartao">
        {carregando && <p className="campo-ajuda">Carregando…</p>}
        {!carregando && respostas.length === 0 && (
          <p className="campo-ajuda">Nenhuma resposta ainda. Crie a primeira abaixo.</p>
        )}

        <ul className="respostas-lista">
          {respostas.map((r) =>
            editando?.id === r.id ? (
              <li key={r.id} className="resposta-linha editando">
                <Formulario
                  dados={editando}
                  aoMudar={setEditando}
                  aoSalvar={() => salvar(editando, r.id)}
                  aoCancelar={() => {
                    setEditando(null);
                    setErro(null);
                  }}
                  salvando={salvando}
                  rotuloSalvar="Salvar"
                  sufixo={r.id}
                />
              </li>
            ) : (
              <li key={r.id} className="resposta-linha">
                <div className="resposta-dados">
                  <strong>
                    <code>{r.atalho}</code> {r.titulo}
                  </strong>
                  <span className="campo-ajuda resposta-texto">{r.texto}</span>
                </div>

                <div className="item-acoes">
                  {confirmando === r.id ? (
                    <>
                      <span className="campo-ajuda">Remover?</span>
                      <button
                        type="button"
                        className="btn-texto perigo"
                        onClick={() => remover(r)}
                        disabled={salvando}
                      >
                        {salvando ? 'Removendo…' : 'Sim, remover'}
                      </button>
                      <button
                        type="button"
                        className="btn-texto"
                        onClick={() => setConfirmando(null)}
                        disabled={salvando}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn-texto"
                        onClick={() => {
                          setErro(null);
                          setConfirmando(null);
                          setEditando({
                            id: r.id,
                            atalho: r.atalho ?? '',
                            titulo: r.titulo ?? '',
                            texto: r.texto ?? '',
                            // Sempre o espaço da ROTA: editar não move de setor.
                            espaco,
                          });
                        }}
                        disabled={salvando}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-texto perigo"
                        onClick={() => {
                          setErro(null);
                          setEditando(null);
                          setConfirmando(r.id);
                        }}
                        disabled={salvando}
                      >
                        Remover
                      </button>
                    </>
                  )}
                </div>
              </li>
            ),
          )}
        </ul>
      </div>

      <div className="cartao">
        <h3 className="secao-titulo pequeno">Nova resposta</h3>
        <p className="campo-ajuda dica-variaveis">
          Dá para usar: <code>{DICA_VARIAVEIS}</code>
        </p>
        <Formulario
          dados={nova}
          aoMudar={setNova}
          aoSalvar={() => salvar(nova, null)}
          salvando={salvando}
          rotuloSalvar="Adicionar"
          sufixo="nova"
        />
      </div>
    </section>
  );
}
